/**
 * Eine Auswertung der Sektion „Anfos & Umsatz": neutrale Überschrift, darunter „So liest du das"
 * (ein bis zwei Sätze für Marketing), der Inhalt (Grafik, Tabelle, Kennzahlen), dann die Begriffe mit
 * ihren Definitionen und am Fuß „Quelle · Stand". Jede Karte trägt alle vier — wer eine Zahl sieht,
 * soll ohne Rückfrage wissen, was sie zählt und wie aktuell sie ist.
 *
 * Begriffe kommen aus `helpers/kpiDefinitions.ts` (TERMS/ATTRIBUTION), nie als Freitext.
 */
import { FC, ReactNode } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import InfoTip from '../InfoTip';
import { formatBerlinWallclock } from '../../helpers/auswertungenData';
import { useAuswertungenMeta } from './ChapterPage';

export interface CardTerm {
  label: string;
  tip: string;
}

interface ChartCardProps {
  title: string;
  /**
   * Kleine graue Zeile unter dem Titel (P5-Review #29), z. B. der Zeitraum „18.10.2026–25.10.2026" — im Titel brach er
   * bei 390 px auf drei Zeilen.
   */
  subtitle?: ReactNode;
  /** „So liest du das" — was die Darstellung zeigt und worauf man achten soll. */
  howToRead: ReactNode;
  children: ReactNode;
  /** Begriffe, die in der Karte vorkommen, mit Definition. */
  terms?: CardTerm[];
  /** Woher die Zahlen kommen, in Worten. */
  source: string;
  /**
   * Welche Quelle(n) den Stand bestimmen (Keys aus /api/auswertungen/meta). Default PL-X-Bestellungen.
   * Mehrere: je Quelle ein Stand mit Namen.
   */
  freshnessKey?: string | string[];
  actions?: ReactNode;
  /** Sprungziel für Links von anderen Seiten (`/auswertungen/kanaele#direkte-bestell-links`). */
  anker?: string;
}

const ChartCard: FC<ChartCardProps> = ({
  title,
  subtitle,
  howToRead,
  children,
  terms,
  source,
  freshnessKey = 'plx_orders',
  actions,
  anker,
}) => {
  const meta = useAuswertungenMeta();
  const keys = Array.isArray(freshnessKey) ? freshnessKey : [freshnessKey];
  const staende = keys.map((k) => {
    const s = meta?.sources.find((x) => x.key === k);
    return { key: k, label: s?.label ?? k, stand: s?.updated_at ?? null };
  });
  return (
    <Paper
      id={anker}
      variant="outlined"
      sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: '8px', backgroundColor: 'transparent', scrollMarginTop: 16 }}
    >
      {/* Umschalter rechts umbrechen unter den Titel, statt ihn zu quetschen (Hebel 390 px: Titel 54 px breit, ein Wort je
          Zeile, Runde 2). Der Titel nimmt mindestens 220 px, sonst wandern die Aktionen in die nächste Zeile. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ minWidth: 0, flex: '1 1 220px' }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions}
      </Box>
      <Typography variant="body2" sx={{ mt: 1, mb: 2, color: 'text.secondary', maxWidth: '68ch', lineHeight: 1.6 }}>
        <Box component="span" sx={{ fontWeight: 600 }}>
          So liest du das:{' '}
        </Box>
        {howToRead}
      </Typography>
      {children}
      {terms && terms.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
          {terms.map((t) => (
            <Box key={t.label} sx={{ display: 'inline-flex', alignItems: 'center', fontSize: 13 }}>
              {t.label}
              <InfoTip text={t.tip} />
            </Box>
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 3, rowGap: 0.5, mt: 2 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Quelle: {source}
        </Typography>
        {staende.map((s) => (
          <Typography key={s.key} variant="caption" sx={{ color: 'text.secondary' }}>
            Stand{staende.length > 1 ? ` ${s.label}` : ''}: {formatBerlinWallclock(s.stand)}
          </Typography>
        ))}
      </Box>
    </Paper>
  );
};

export default ChartCard;
